import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Avatar, avatarSrc } from '../components/DashboardLayout';
import { Camera, Trash2, Save, CheckCircle, AlertCircle, User as UserIcon, Mail, Shield } from 'lucide-react';
import NotificationPreferences from '../components/NotificationPreferences';
import ChangePasswordCard from '../components/ChangePasswordCard';
import DeleteAccountCard from '../components/DeleteAccountCard';
import { FileText, LifeBuoy } from 'lucide-react';
import { isNativeApp } from '../lib/native';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = {
  super_admin: 'Super Admin',
  company_admin: 'Company Admin',
  dispatcher: 'Dispatcher',
  driver: 'Driver',
};

const Profile = () => {
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await axios.put(`${API}/auth/profile`, { name, bio }, { withCredentials: true });
      setUser(res.data);
      showToast('success', 'Profile updated');
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showToast('error', 'Image must be under 2MB');
      return;
    }
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/auth/avatar`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Bust cache by appending timestamp
      setUser({ ...user, avatar_url: `${res.data.avatar_url}?t=${Date.now()}` });
      showToast('success', 'Profile picture updated');
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingAvatar(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  useEffect(() => { if (window.location.hash) { const el = document.getElementById(window.location.hash.slice(1)); if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150); } }, []);
  const handleAvatarDelete = async () => {
    if (!window.confirm('Remove your profile picture?')) return;
    try {
      await axios.delete(`${API}/auth/avatar`, { withCredentials: true });
      setUser({ ...user, avatar_url: null });
      showToast('success', 'Profile picture removed');
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Failed to remove');
    }
  };

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-3xl mx-auto" data-testid="profile-page">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>My Profile</h1>
        <p className="text-base text-slate-600 mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Update your photo, name, and bio.</p>

        {toast && (
          <div
            data-testid="profile-toast"
            className={`mb-4 px-4 py-3 rounded-md text-sm flex items-center gap-2 border ${toast.kind === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {toast.kind === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {toast.msg}
          </div>
        )}

        {/* Avatar section */}
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6" data-testid="profile-avatar-section">
          <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>Profile Picture</h2>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="relative">
              {avatarSrc(user) ? (
                <img src={avatarSrc(user)} alt="" className="w-24 h-24 rounded-full object-cover ring-4 ring-[#002FA7]/10" data-testid="profile-avatar-img" />
              ) : (
                <Avatar user={user} size={24} ringColor="ring-[#002FA7]/10" />
              )}
              <button
                data-testid="upload-avatar-button"
                onClick={() => fileRef.current?.click()}
                disabled={uploadingAvatar}
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#002FA7] hover:bg-[#00247D] text-white rounded-full flex items-center justify-center shadow-md transition-all disabled:opacity-50"
                title="Upload new picture"
              >
                <Camera className="w-4 h-4" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarChange}
                className="hidden"
                data-testid="avatar-file-input"
              />
            </div>
            <div className="space-y-1 text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <p>JPG, PNG, or WEBP. Max 2MB.</p>
              {user.avatar_url && (
                <button
                  data-testid="remove-avatar-button"
                  onClick={handleAvatarDelete}
                  className="text-red-600 hover:text-red-700 font-semibold flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Remove picture
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Account info (read-only) */}
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
          <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>Account</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <ReadOnlyField icon={Mail} label="Email" value={user.email} />
            <ReadOnlyField icon={Shield} label="Role" value={ROLE_LABEL[user.role] || user.role} />
          </dl>
        </div>

        {/* Editable fields */}
        <form onSubmit={handleSave} className="bg-white border border-slate-200 rounded-md p-6">
          <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Work Sans, sans-serif' }}>Public Info</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Display Name</label>
              <div className="relative">
                <UserIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="name-input"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-[#002FA7]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Bio <span className="text-slate-400 font-normal lowercase">({bio.length}/500)</span>
              </label>
              <textarea
                data-testid="bio-input"
                rows="4"
                maxLength={500}
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="A short bio your teammates will see..."
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-[#002FA7]"
              />
            </div>
          </div>

          <div className="flex items-center justify-end mt-6">
            <button
              data-testid="save-profile-button"
              type="submit"
              disabled={savingProfile}
              className="bg-[#002FA7] text-white px-5 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all disabled:opacity-50 flex items-center gap-2"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <Save className="w-4 h-4" />
              {savingProfile ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        <div id="password" /><ChangePasswordCard />

        <div id="notifications" /><NotificationPreferences userEmail={user.email} />

        <div className="bg-white border border-slate-200 rounded-md p-6 mt-6" data-testid="support-legal-card">
          <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-3" style={{ fontFamily: 'Work Sans, sans-serif' }}>Support &amp; Legal</h2>
          <div className="flex flex-col sm:flex-row gap-3 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <a href="/privacy-policy" target={isNativeApp() ? undefined : "_blank"} rel="noreferrer" data-testid="profile-privacy-policy-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-800 font-medium">
              <FileText className="w-4 h-4 text-[#002FA7]" /> Privacy Policy
            </a>
            <a href="/terms" target={isNativeApp() ? undefined : "_blank"} rel="noreferrer" data-testid="profile-terms-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-800 font-medium">
              <FileText className="w-4 h-4 text-[#002FA7]" /> Terms of Service
            </a>
            <a href="mailto:kollabsystems@gmail.com?subject=CivilX%20TMS%20support" data-testid="profile-support-link"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-800 font-medium">
              <LifeBuoy className="w-4 h-4 text-[#002FA7]" /> Contact support
            </a>
          </div>
        </div>

        <div id="delete-account" /><DeleteAccountCard />
      </div>
    </DashboardLayout>
  );
};

const ReadOnlyField = ({ icon: Icon, label, value }) => (
  <div>
    <dt className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-1">
      <Icon className="w-3 h-3" /> {label}
    </dt>
    <dd className="text-sm text-slate-900 font-medium">{value || '—'}</dd>
  </div>
);

export default Profile;
